import React, { useState } from 'react';
import { GridToolbarContainer, GridToolbarFilterButton, useGridApiContext } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import DataTable from '../../../../../@Common/DataTable';
import { Chip } from '@mui/material';
import { FileUpload } from '@mui/icons-material';
import EligibilityIconList from './EligibilityIconList';
import StyledLink from '../../../../../StyledLink';
import OfferOptionsMenu from './OfferOptionsMenu';
import NoRows from '../../../../../@Common/DataTable/NoRows';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getOfferType, offerExportColumns } from './offerExportColumns'
import { formatDiscount, getOfferDetailsValue } from './offerDetails'

function CustomToolbar({ onImportOffersClick, onClickExportOffers }) {
    return (
        <GridToolbarContainer sx={{ padding: '4px 4px 4px', gap: '0px' }}>
            <GridToolbarFilterButton sx={{ textTransform: 'none', fontSize: '0.8125rem' }} />
            <Button
                onClick={onImportOffersClick}
                sx={{ ml: 1, fontSize: '0.8125rem', textTransform: 'none' }}
                title="Import Offers"
            >
                <FileUpload /> Import
            </Button>
            <CustomToolbarExport onClickExportOffers={onClickExportOffers} />
        </GridToolbarContainer>
    );
}

function CustomToolbarExport({ onClickExportOffers }) {
    const apiRef = useGridApiContext()

    return (
        <Button
            onClick={() => onClickExportOffers(apiRef)}
            sx={{
                '& .MuiSvgIcon-root': {
                    fontSize: 24,
                },
                '& .MuiButton-startIcon': {
                    marginRight: '2px',
                },
                ml: 1,
                fontSize: '0.8125rem',
                textTransform: 'none'
            }}
            title="Export Offers"
        >
            <FileDownloadIcon /> Export
        </Button>
    )
}

const OffersGrid = ({
    offers,
    offersIsSuccess,
    onClickCommissionRateHistoryModal,
    onClickSyndicatedListingsModal,
    onClickEditOffer,
    openGovXIdLinkModal,
    openCreateTestRedemptionSessionModal,
    canCreateTestRedemptionSession,
    canDeleteOffer,
    canDeleteSyndicateOffer,
    canSyncOffer,
    canSyndicateOffer,
    canViewSyndicatedListings,
    onClickPublish,
    onClickUnpublish,
    openPublishModal,
    onClickDelete,
    onDelete,
    onClickSyncOffer,
    onClickSyndicateOffer,
    canSetStatus,
    prefetchOfferDetails,
    channels,
    isLoading,
    isFetching,
    onChangeStatus,
    onClickSyndicatedDetailsModal,
    onClickRemoveSyndicateOffer,
    onClickImportOffers,
    getLinkedEventsByOfferIds
}) => {
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filters, setFilters] = useState({
        items: [
        ],
        logicOperator: 'and',
    })

    const handleExport = async (offers, apiRef) => {
        let offersWithLinkedEvents = await getLinkedEventsByOfferIds(offers)
        let columnVisibilityModel = {}
        let tempColumns = [...columns]
        const exportFields = offerExportColumns.map(col => col.field);
        offerExportColumns.forEach(col => columnVisibilityModel[col.field] = false);
        offerExportColumns.forEach(col => tempColumns.push({ ...col, filterable: false }));

        let maxEvents = offersWithLinkedEvents ? offersWithLinkedEvents.reduce((max, offer) => {
            return offer.events && offer.events.length > max ? offer.events.length : max;
        }, 0) : 0;

        for (let i = 0; i < maxEvents; i++) {
            const header = `linked_event_${i + 1}`
            tempColumns.push({
                field: header,
                valueGetter: (params) => params.row.events?.[i]?.id,
                filterable: false,
            })
            exportFields.push(header)
            columnVisibilityModel[header] = false
        }
        setColumnVisibilityModel(columnVisibilityModel);
        apiRef.current.updateRows(offersWithLinkedEvents);
        apiRef.current.updateColumns(tempColumns);
        return apiRef.current.exportDataAsCsv({ fields: exportFields })
    }

    const columns = [
        { field: 'name', headerName: 'Offer', flex: 1, minWidth: 200 },
        {
            field: 'discount',
            headerName: 'Details',
            width: 300,
            filterable: false,
            valueGetter: (params) => getOfferDetailsValue(params.row),
            renderCell: (params) => {
                const type = getOfferType(params.row.discount)
                const discount = formatDiscount(params.row.discount)
                const suffix = discount ? ` — ${discount}` : ''
                if (params.row.source?.type?.toLowerCase() === 'shopify') {
                    return <>Shopify{suffix}</>
                }
                else if (type.key === 'codePool') {
                    return <>
                        <StyledLink
                            styles={{ marginRight: '2px' }}
                            to={`/code-pools/${params.row.codePool?.id}`}
                            contents={`Code Pool`}
                            newWindow={true} />
                        ({(params.row.codePool?.statistics.totalActive - params.row.codePool?.statistics.totalReserved)} remaining){suffix}</>
                }
                else {
                    return <>{type.name}{suffix}</>
                }
            }
        },
        {
            field: 'verification',
            headerName: 'Eligibility',
            width: 200,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <div>
                        <EligibilityIconList eligibleGroups={params.row.verification.eligibleGroups} />
                    </div>
                )
            }


        },
        {
            field: 'channels',
            headerName: 'Sales Channels',
            width: 200,
            type: 'singleSelect',
            valueOptions: ['All Channels', 'GOVX', 'Offer Page', 'No Channels'],
            sortable: false,
            valueGetter: (params) => {
                const salesChannels = params.row.channels.map(x => channels.find(channel => channel.value === x)?.text)
                if (salesChannels.length > 1) {
                    return 'All Channels'
                } else if (salesChannels.length === 0) {
                    return 'No Channels'
                } else {
                    return salesChannels[0]
                }
            },
            renderCell: (params) =>
                params.row.channels?.length > 0 ? params.row.channels.map((channel) => <Chip key={channel} sx={{ marginRight: '5px' }} label={channels?.find((x) => x.value === channel)?.text} size="small" variant="filled" color="default" />)
                    : 'None'

        },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            type: 'singleSelect',
            valueOptions: ['Published', 'Unpublished'],
        },
        {
            field: '',
            headerName: '',
            width: 20,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params) => {
                const offer = params.row;
                return (
                    <OfferOptionsMenu
                        offer={offer}
                        onClickCommissionRateHistoryModal={onClickCommissionRateHistoryModal}
                        onClickSyndicatedListingsModal={onClickSyndicatedListingsModal}
                        onClickEditOffer={onClickEditOffer}
                        openGovXIdLinkModal={openGovXIdLinkModal}
                        onClickCreateTestRedemptionSession={() => openCreateTestRedemptionSessionModal(offer)}
                        canCreateTestRedemptionSession={canCreateTestRedemptionSession}
                        onClickPublish={() => { onClickPublish(offer, true, onChangeStatus) }}
                        onClickUnpublish={() => { onClickUnpublish(offer, false, onChangeStatus) }}
                        openPublishModal={openPublishModal}
                        onClickDelete={() => onClickDelete(offer, onDelete)}
                        onClickSyncOffer={onClickSyncOffer}
                        onClickSyndicateOffer={onClickSyndicateOffer}
                        canDeleteSyndicateOffer={canDeleteSyndicateOffer}
                        canSyncOffer={canSyncOffer}
                        canDeleteOffer={canDeleteOffer}
                        canSetStatus={canSetStatus}
                        canSyndicateOffer={canSyndicateOffer}
                        canViewSyndicatedListings={canViewSyndicatedListings}
                        onClickSyndicatedDetailsModal={onClickSyndicatedDetailsModal}
                        onClickRemoveSyndicateOffer={onClickRemoveSyndicateOffer}
                    />

                );
            },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <DataTable
                localeText={{
                    toolbarFilters: 'Filter', // This changes the GridToolbarFilterButton label
                }}
                rowCount={offers ? offers.length : 0}
                getRowId={(row) => row.offerId}
                rows={offers ? offers : []}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => setPaginationModel(model)}
                pageSizeOptions={[25, 50, 100]}
                onRowHover={(params) => prefetchOfferDetails(params.row)}
                noRowsOverlay={<NoRows message={'There are no offers for this listing.'} />}
                customToolbar={<CustomToolbar onImportOffersClick={onClickImportOffers} onClickExportOffers={(apiRef) => handleExport(offers, apiRef)} />}
                hideFooterWhenNoRows={true}
                additionalCustomSlots={{
                    exportIcon: FileDownloadIcon,
                }}
                loading={isLoading || isFetching}
                filterModel={{
                    items: filters.items,
                    logicOperator: filters.logicOperator,
                }}
                onFilterModelChange={(model) => {
                    setFilters({
                        items: model.items,
                        logicOperator: model.logicOperator,
                    })
                }}
            />
        </div>
    );
};

export default OffersGrid;